import 'lodash';
import { isValidUrl } from '../../utils/common';
import ColorLoader from './color-loader';
import { FontLoader, loadCustomFont, loadGoogleFont, loadUserFont } from './font-loader';
import TemplateMetadata from './template-metadata';
import { TextLoader } from './text-loader';
import { SiteFonts } from '../../types/metadata';
export class TemplateLoader {
    node;
    originalHTMLString;
    _templateMetaData;
    colorLoader;
    fontsLoader;
    textLoader;
    cachedHTML;
    constructor(node) {
        this.node = node;
        this.originalHTMLString = node.innerHTML;
    }
    get originalHTML() {
        if (this.originalHTMLString === '' || this.originalHTMLString === null || this.originalHTMLString === undefined)
            throw new Error('Original HTML string is empty');
        return this.originalHTMLString;
    }
    validateHTMLString(htmlString) {
        return true; // TODO: Create an html string validator
    }
    recomputeTemplateMetadata() {
        this._templateMetaData.recomputeTemplateDimensions();
    }
    cleanupTemplate() {
        this.node
            .querySelectorAll('.glyphicon.editButton,#imageBorder,.centerGridline,.editButton')
            .forEach(node => node.remove());
        this.node.querySelectorAll('.playingNow').forEach(node => node.classList.remove('playingNow'));
        this.node.querySelectorAll('.dynamic').forEach(node => node.removeAttribute('contenteditable'));
    }
    get templateMetadata() {
        return this._templateMetaData?.info;
    }
    async fixLegacyIssues() {
        console.log('Fixing legacy issues');
        this.developmentModeFixTemplateURLIssuesForAssets();
        const textElements = this.node.querySelectorAll('[data-amelement="caption"],[data-amelement="sermon title"]');
        // textElementsStore.set([...textElements, ...textElements]);
        const captionElement = this.node.querySelector('[data-amelement="caption"]');
        if (captionElement) {
            captionElement.style.padding = '0';
            captionElement.style.overflow = '';
            const dynamicElement = captionElement.querySelector('.dynamic');
            if (dynamicElement) {
                dynamicElement.style.padding = '0';
                dynamicElement.style.overflow = '';
            }
        }
        this.removeContentEditableOnTextElements(textElements);
        this.enforceTextElementHeight(textElements);
        try {
            await this.loadCustomFonts();
        }
        catch (err) {
            console.warn('Could not load custom fonts');
        }
    }
    /**
     * Important note:
     * The reason we need to fix bounding client rectangles for some instances is that we will need the fixed height of the elements later down the road to enforce size limitations
     * In an instance where the element is auto height, if the captions generated dynamically are too long, the height of the element will increase and the element will be larger than the size limitation
     * @param textElements
     */
    enforceTextElementHeight(textElements) {
        textElements.forEach(textElement => {
            if (textElement) {
                const captionStyles = textElement.style;
                if (captionStyles.height == 'auto' || captionStyles.height == '') {
                    const captionRect = textElement.getBoundingClientRect();
                    textElement.style.height = captionRect.height + 'px';
                }
            }
        });
    }
    removeContentEditableOnTextElements(textElements) {
        textElements.forEach(element => {
            element.removeAttribute('contenteditable');
            try {
                element.querySelector('.dynamic')?.removeAttribute('contenteditable');
            }
            catch (err) {
                console.warn('Could not remove the content editable', err);
            }
        });
    }
    developmentModeFixTemplateURLIssuesForAssets() {
        this.node.querySelectorAll('.stampImg, source').forEach(stamp => {
            const src = stamp.getAttribute('src');
            if (!isValidUrl(src))
                stamp.setAttribute('src', 'https://sermonshots.com' + src);
        });
    }
    existsInSiteFonts(fontName) {
        if (SiteFonts.includes(fontName))
            return true;
        return false;
    }
    async loadCustomFonts() {
        console.log('[Template Loader] Loading custom fonts');
        let textElements = this.node.querySelectorAll('.userTextClass .dynamic');
        let promises = [];
        let fontsParsed = [];
        textElements.forEach(element => {
            const customFontLocation = element.getAttribute('data-nameoffontfile');
            const customFontName = element.getAttribute('data-customfontname');
            const userFontname = element.getAttribute('data-user-fontname');
            const userFontLocation = element.getAttribute('data-user-fonturl');
            const existsInSiteFonts = this.existsInSiteFonts(customFontName);
            if (userFontLocation && userFontLocation !== '' && userFontname && userFontname !== '' && !existsInSiteFonts) {
                if (fontsParsed.includes(userFontname))
                    return;
                fontsParsed.push(userFontname);
                const customFontPromise = loadUserFont(userFontname, userFontLocation);
                promises.push(customFontPromise);
            }
            else if (customFontLocation && customFontLocation !== '' && !existsInSiteFonts) {
                if (fontsParsed.includes(customFontName))
                    return;
                fontsParsed.push(customFontName);
                const fontLoadPromise = loadCustomFont(customFontName, customFontLocation);
                promises.push(fontLoadPromise);
            }
            // By Default we will load all google fonts related to a text element.
            try {
                const fontFamily = element.style.fontFamily.replaceAll(', ', ',');
                const fontArray = fontFamily.split(',').map(item => item.replaceAll('"', ''));
                if (fontArray.length) {
                    console;
                    fontArray.forEach(font => {
                        if (fontsParsed.includes(font))
                            return;
                        fontsParsed.push(font);
                        promises.push(loadGoogleFont(font));
                    });
                }
            }
            catch (err) {
                console.warn('could not load font by default with google font loading', err);
            }
        });
        if (promises.length)
            console.log(`[Template Loader] Loading ${promises.length} custom fonts`);
        await Promise.allSettled(promises);
        return true;
    }
    initializeLoaders(production = false) {
        if (production)
            return;
        this.colorLoader = new ColorLoader(this.node);
        this.fontsLoader = new FontLoader(this.node);
        this.textLoader = new TextLoader(this.node);
    }
    async loadHTML(htmlString, forProduction = false) {
        return new Promise(async (resolve, reject) => {
            if (this.validateHTMLString(htmlString)) {
                this.node.innerHTML = htmlString;
                this.cleanupTemplate();
                await this.fixLegacyIssues();
                if (!forProduction) {
                    setTimeout(() => {
                        this.adjustDisplayForPreview();
                        resolve(true);
                    }, 500);
                }
                this.formatDisplay();
                this.cacheElements();
                this._templateMetaData = new TemplateMetadata(this.node);
            }
            else
                reject(new Error('Invalid html string'));
            if (forProduction) {
                resolve(true);
                this._templateMetaData = new TemplateMetadata(this.node);
            }
        });
    }
    elementOutsideOfParent(element, parentElement) {
        // Determine if the element is outside of the parent element
        const parentRect = parentElement.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        //console.log("rects", parentRect, elementRect);
        if (!parentRect)
            return false;
        if (elementRect.left < parentRect.left - 50 ||
            elementRect.right > parentRect.right + 50 ||
            elementRect.top < parentRect.top - 50 ||
            elementRect.bottom > parentRect.bottom + 50) {
            return true;
        }
        return false;
    }
    getSermonClipDomElement() {
        const parentElement = this.node.querySelector('#finalImageToExport');
        if (!parentElement)
            throw new Error('Parent finalImageToExport not found');
        const elements = this.node.querySelectorAll('[data-amelement="sermonclip"]');
        // get the element that is smaller than the rest
        let smallestElement = null;
        let smallestSize = null;
        let hasMain = false;
        for (const element of elements) {
            //console.log('[getSermonClipdomElement] template scanning element', element);
            if (this.elementOutsideOfParent(element, parentElement)) {
                //console.warn('Element is outside of parent', element, parentElement);
                continue;
            }
            if (hasMain)
                continue;
            if (element.getAttribute('data-notes') && element.getAttribute('data-notes') === 'main') {
                hasMain = true;
                smallestElement = element;
                //console.log('Found main element', smallestElement)
                return smallestElement;
            }
            const size = element.getBoundingClientRect().width * element.getBoundingClientRect().height;
            //console.log('Computed size', size, smallestElement, smallestSize)
            if (smallestSize == null || size < smallestSize) {
                smallestElement = element;
                smallestSize = size;
            }
        }
        if (!smallestElement) {
            if (elements.length)
                return elements[0];
            // else throw new Error("There is no sermon clip element.");
        }
        //console.log('[getSermonClipdomElement] template scanning element result', smallestElement)
        return smallestElement;
    }
    clearPreviousSermonClipElement() {
        const el = this.node.querySelector('[sermonclip]');
        if (el) {
            try {
                el.removeAttribute('sermonclip');
                el.removeAttribute('sermonclip-left');
                el.removeAttribute('sermonclip-top');
                el.removeAttribute('sermonclip-width');
                el.removeAttribute('sermonclip-height');
            }
            catch (err) {
                console.warn('[Could not clear out old sermonclip element');
            }
        }
    }
    cacheElements() {
        this.clearPreviousSermonClipElement();
        const sermonClip = this.getSermonClipDomElement();
        if (!sermonClip)
            return;
        if (sermonClip.getAttribute('sermonclip') === 'true')
            return;
        const styles = getComputedStyle(sermonClip);
        sermonClip?.setAttribute('sermonclip', 'true');
        sermonClip.setAttribute('sermonclip-left', styles.left);
        sermonClip.setAttribute('sermonclip-top', styles.top);
        sermonClip.setAttribute('sermonclip-width', styles.width);
        sermonClip.setAttribute('sermonclip-height', styles.height);
        this.cachedHTML = this.node.innerHTML;
    }
    getAmElement(title) {
        return this.node.querySelector(`[data-amelement="${title}"]`);
    }
    formatDisplay() {
        const node = this.node.querySelector('#finalImageToExport');
        if (!node)
            throw 'Final image to export does not exist.';
        const overwriteStyles = {
            position: 'relative',
            backgroundColor: 'white'
        };
        Object.assign(node.style, overwriteStyles);
        // if (this.metadata) this.buildMetadataDisplay();
    }
    adjustDisplayForPreview() {
        // console.log('Adjusting display for preview');
        const templateDiv = this.node.querySelector('#templateLoaded');
        const finalImageToExport = this.node.querySelector('#finalImageToExport');
        const targetWidth = Number.parseInt(templateDiv?.getAttribute('fx'));
        const finalTargetHeight = Number.parseInt(templateDiv?.getAttribute('fy'));
        const finalWidth = templateDiv?.getAttribute('fx');
        if (!templateDiv || !finalImageToExport)
            throw new Error('Template div or final image to export not found');
        let fx = finalImageToExport.offsetWidth;
        let fy = finalImageToExport.offsetHeight;
        const aspectRatio = fx / fy;
        Object.assign(finalImageToExport.dataset, {
            ofx: fx,
            ofy: fy
        });
        const parent = this.node.parentElement;
        const styles = getComputedStyle(parent);
        const bkgContainerWidth = templateDiv.getAttribute('bkgImageContainterWidth');
        const recordedWidth = Number.parseInt(bkgContainerWidth);
        if (!bkgContainerWidth)
            throw new Error('No bkgContainerWidth found');
        let tx = parent.clientWidth - (parseInt(styles.paddingLeft) + parseInt(styles.paddingRight));
        let ty = parent.clientHeight - (parseInt(styles.paddingTop) + parseInt(styles.paddingBottom));
        var targetHeight = ty > finalTargetHeight ? finalTargetHeight : ty;
        let ratio;
        // console.log('sample ratio', fy/ty);
        // const newWidth = parseFloat(finalImageToExport.style.width) / ratio;
        // const newHeight = parseFloat(finalImageToExport.style.height) / ratio;
        let newWidth = targetHeight * aspectRatio;
        if (newWidth > tx) {
            newWidth = tx;
            targetHeight = newWidth / aspectRatio;
        }
        const rescaleRatio = recordedWidth / newWidth;
        finalImageToExport.style.width = newWidth + 'px';
        finalImageToExport.style.height = targetHeight + 'px';
        // console.log('Compare recorded and currentWidth', recordedWidth, fx);
        // console.log('[Template Loader] Adjusting preview sizes with ratio', ratio);
        // console.log(fy, fx, ty, tx, newWidth, targetHeight);
        adjustPreviewSizes(rescaleRatio, '#finalImageToExport');
        this.recomputeTemplateMetadata();
        Object.assign(finalImageToExport.dataset, {
            fx: finalImageToExport.style.width,
            fy: finalImageToExport.style.height
        });
        templateDiv.setAttribute('bkgimagecontainterwidth', newWidth.toString());
        return this;
    }
}
